/**
 * Utility to handle font loading and add a 'fonts-loaded' class
 * to the document once fonts are loaded
 */

/**
 * Font loading status
 */
export type FontLoadStatus = 'loading' | 'loaded' | 'error';

/**
 * Configuration for font loading
 */
export interface FontLoaderConfig {
  /**
   * Debug mode for logging
   */
  debug?: boolean;

  /**
   * Timeout for font loading in milliseconds
   */
  timeout?: number;

  /**
   * Callback when fonts are loaded
   */
  onLoaded?: () => void;

  /**
   * Callback when font loading fails
   */
  onError?: (error: Error) => void;
}

/**
 * Current font loading status
 */
let currentStatus: FontLoadStatus = 'loading';

/**
 * Initialize font loading
 */
export function initFontLoader({
  debug = false,
  timeout = 3000,
  onLoaded,
  onError,
}: FontLoaderConfig = {}): void {
  const log = (message: string): void => {
    if (debug) {
      console.debug(`[FontLoader] ${message}`);
    }
  };

  log('Initializing font loader');

  // If document.fonts is not available, fallback to timeout
  if (!('fonts' in document)) {
    log('document.fonts API not available, using fallback');

    // Set a timeout to add the class anyway
    setTimeout(() => {
      document.documentElement.classList.add('fonts-loaded');
      currentStatus = 'loaded';
      log('Fonts assumed loaded (fallback)');
      onLoaded?.();
    }, timeout);

    return;
  }

  // Set a timeout in case font loading takes too long
  const timeoutId = setTimeout(() => {
    if (currentStatus === 'loading') {
      log(`Font loading timed out after ${timeout}ms`);
      document.documentElement.classList.add('fonts-loaded');
      currentStatus = 'loaded';
      onLoaded?.();
    }
  }, timeout);

  // Wait for fonts to load
  document.fonts.ready
    .then(() => {
      if (currentStatus === 'loading') {
        log('Fonts loaded successfully');
        document.documentElement.classList.add('fonts-loaded');
        currentStatus = 'loaded';
        clearTimeout(timeoutId);
        onLoaded?.();
      }
    })
    .catch((error: Error) => {
      log(`Error loading fonts: ${error.message}`);
      document.documentElement.classList.add('fonts-loaded');
      currentStatus = 'error';
      clearTimeout(timeoutId);
      onError?.(error);
    });
}

/**
 * Get the current font loading status
 */
export function getFontLoadStatus(): FontLoadStatus {
  return currentStatus;
}
